/*
 * <product-accessories> — Cross-Sell-/Add-on-Liste unter dem Produkt.
 * Quelle: Metafeld pimcore.zubehoer (in Pimcore am Produkt gepflegt).
 * Auswahl per Checkbox + Menge; Einzel-Add oder Sammel-Add der ausgewählten
 * Zeilen über /cart/add.js (Multi-Item). Nutzt denselben Cart-Drawer und die
 * globalen Helfer (fetchConfig/publish/PUB_SUB_EVENTS) wie variant-matrix.js.
 */
if (!customElements.get('product-accessories')) {
  customElements.define(
    'product-accessories',
    class ProductAccessories extends HTMLElement {
      connectedCallback() {
        this.cartAddUrl =
          this.dataset.cartAddUrl || (window.routes && window.routes.cart_add_url) || '/cart/add';
        this.cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.errorEl = this.querySelector('.product-accessories__error');
        this.grandEls = Array.from(this.querySelectorAll('.pa-grand-value'));
        this.addAllBtn = this.querySelector('.pa-add-all');
        this.items = Array.from(this.querySelectorAll('.product-accessories__item'));

        this.items.forEach((item) => {
          const check = item.querySelector('.pa-check');
          const qty = item.querySelector('.pa-qty');
          const sel = item.querySelector('select.pa-variant');
          const addBtn = item.querySelector('.pa-add');

          if (check) {
            check.addEventListener('change', () => {
              // Pflicht-Zubehör (required) bleibt ausgewählt – nur wenn verfügbar (nicht disabled).
              // Kein HTML-disabled auf required, damit isChecked() es weiterhin zählt.
              if (check.dataset.required === 'true' && !check.checked && !check.disabled) {
                check.checked = true;
              }
              this.onChange();
            });
          }
          if (qty) {
            qty.addEventListener('change', () => this.onChange());
            qty.addEventListener('input', () => this.onChange());
          }
          if (sel) sel.addEventListener('change', () => this.onVariantChange(item));
          if (addBtn) addBtn.addEventListener('click', () => this.addSingle(item, addBtn));
        });

        if (this.addAllBtn) this.addAllBtn.addEventListener('click', () => this.addSelected());

        this.sortVariantOptions();
        this.initCustomSelects();
        this.initSelectAll();
        this.initDetailsToggles();
        this.initQuickView();
        this.onChange();
        this.initB2BPrices();
      }

      // Ausführungen (Varianten-Dropdown) numerisch aufsteigend sortieren — z. B. 500 mm < 1000 mm < 1200 mm
      // (Shopify liefert die Positions-Reihenfolge, die alphabetisch/inkonsistent sein kann). Nur sortieren,
      // wenn jede Option eine Zahl enthält; sonst Reihenfolge beibehalten. Ordnet <li> UND natives <option> um.
      sortVariantOptions() {
        const parseNum = (t) => {
          const m = (t || '').replace(/ /g, ' ').match(/-?\d+(?:[.,]\d+)?/);
          return m ? parseFloat(m[0].replace(',', '.')) : NaN;
        };
        this.querySelectorAll('.pa-cselect').forEach((cs) => {
          const native = cs.querySelector('select.pa-cselect__native');
          const list = cs.querySelector('.pa-cselect__list');
          if (!native || !list) return;
          const opts = Array.from(list.querySelectorAll('.pa-cselect__opt'));
          if (opts.length < 2) return;
          const nums = opts.map((o) => parseNum(o.textContent));
          if (nums.some((n) => Number.isNaN(n))) return; // gemischte/nicht-numerische Titel: unverändert
          const order = opts.map((o, i) => i).sort((a, b) => nums[a] - nums[b]);
          const optionByValue = {};
          Array.from(native.options).forEach((op) => {
            optionByValue[op.value] = op;
          });
          order.forEach((idx) => {
            list.appendChild(opts[idx]); // <li> neu anordnen (selected/aria bleiben erhalten)
            const val = opts[idx].dataset.value;
            if (optionByValue[val]) native.appendChild(optionByValue[val]); // <option> analog
          });
        });
      }

      // Titel/Bild öffnen ein Info-Popup mit demselben Inhalt wie das „Details"-Panel
      // (Beschreibung + techn. Daten inkl. variantenspezifisch + Downloads) — geklont, eine
      // Datenquelle. Nur das Popup scrollt (nie die Seite dahinter). Ohne JS bleiben es normale
      // Links zur Produktseite; „Zur Produktseite" im Popup führt ebenfalls dorthin.
      initQuickView() {
        this.modal = this.querySelector('.pa-modal');
        if (this.modal) {
          this.modalTitle = this.modal.querySelector('.pa-modal__title');
          this.modalBody = this.modal.querySelector('.pa-modal__body');
          this.modalLink = this.modal.querySelector('.pa-modal__link');
          this.modal.querySelectorAll('[data-pa-close]').forEach((el) =>
            el.addEventListener('click', () => this.closeQuickView())
          );
          document.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape') return;
            // ⚠️ Reihenfolge: liegt die Bild-Grossansicht oben, schliesst Escape NUR sie —
            // sonst faellt die Schnellansicht darunter gleich mit weg (24.08.2026).
            if (this.imgModal && !this.imgModal.hidden) {
              this.closeImgModal();

              return;
            }
            this.closeQuickView();
          });
        }
        // Bild-Grossansicht: Gegenstueck zur Lightbox der Variantenmatrix. Eigener Dialog, weil
        // dieser Block ausserhalb von <variant-matrix> liegt und deren Delegation nicht erreicht.
        this.imgModal = this.querySelector('.pa-img-modal');
        if (this.imgModal) {
          this.imgModalImg = this.imgModal.querySelector('.pa-img-modal__img');
          this.imgModalCaption = this.imgModal.querySelector('.pa-img-modal__caption');
          this.zoomLabel = this.imgModal.dataset.i18nZoom || '';
          this.imgModal.querySelectorAll('[data-pa-img-close]').forEach((el) =>
            el.addEventListener('click', () => this.closeImgModal())
          );
        }
        this.querySelectorAll('.pa-quickview').forEach((trigger) => {
          trigger.addEventListener('click', (e) => {
            if (!this.modal) return; // Fallback: Link folgt zur Produktseite
            e.preventDefault();
            this.openQuickView(trigger);
          });
        });
      }

      openQuickView(trigger) {
        const item = trigger.closest('.product-accessories__item');
        if (!item) return;
        const nameEl = item.querySelector('.product-accessories__name');
        const detail = item.querySelector('.product-accessories__detail');
        const url = trigger.getAttribute('data-product-url') || (nameEl && nameEl.getAttribute('href')) || '#';

        // Titel + Inhalt (Klon des Detail-Panels: Beschreibung + Tech inkl. gewählter Variante + Downloads)
        this.modalTitle.textContent = nameEl ? nameEl.textContent.trim() : '';
        this.modalBody.innerHTML = detail
          ? detail.innerHTML
          : '<p>' + (this.modalTitle.textContent || '') + '</p>';

        // Produktbild (wie auf der Produktseite) — data-image am <li>
        const media = this.modal.querySelector('.pa-modal__media');
        const grid = this.modal.querySelector('.pa-modal__grid');
        const img = item.getAttribute('data-image');
        if (media) {
          media.innerHTML = '';
          media.onmousemove = media.onmouseleave = null;
          if (img) {
            const imgEl = document.createElement('img');
            imgEl.src = img;
            imgEl.alt = item.getAttribute('data-image-alt') || '';
            if (this.imgModal) {
              // ⚠️ Button statt Klick-Handler am <img>: sonst ist die Grossansicht nicht per
              // Tastatur erreichbar. Der Lupen-Zoom bleibt an .pa-modal__media haengen.
              const zoomBtn = document.createElement('button');
              zoomBtn.type = 'button';
              zoomBtn.className = 'pa-img-zoom';
              zoomBtn.setAttribute(
                'aria-label',
                (this.zoomLabel ? this.zoomLabel + ': ' : '') + (imgEl.alt || '')
              );
              zoomBtn.addEventListener('click', () => this.openImgModal(img, imgEl.alt || ''));
              zoomBtn.appendChild(imgEl);
              media.appendChild(zoomBtn);
            } else {
              media.appendChild(imgEl);
            }
            media.classList.add('pa-modal__media--zoom');
            // Lupen-Zoom: das echte Bild um den Cursor herum skalieren (transform-origin folgt dem
            // Zeiger relativ zum BILD, nicht zum gepolsterten Media-Kasten → immer deckungsgleich).
            const ZOOM = 2.2;
            const move = (e) => {
              const r = imgEl.getBoundingClientRect();
              const x = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
              const y = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
              imgEl.style.transformOrigin = x + '% ' + y + '%';
              imgEl.style.transform = 'scale(' + ZOOM + ')';
            };
            media.onmousemove = move;
            media.onmouseleave = () => {
              imgEl.style.transform = '';
            };
            if (grid) grid.classList.remove('is-single');
          } else {
            media.classList.remove('pa-modal__media--zoom');
            if (grid) grid.classList.add('is-single');
          }
        }

        if (this.modalLink) this.modalLink.setAttribute('href', url);
        this.modal.hidden = false;
        document.body.classList.add('overflow-hidden');
        const dialog = this.modal.querySelector('.pa-modal__dialog');
        if (dialog) dialog.focus();
        this.modalBody.scrollTop = 0;
      }

      openImgModal(src, title) {
        if (!this.imgModal || !this.imgModalImg || !src) return;
        this.imgModalImg.src = src;
        this.imgModalImg.alt = title || '';
        if (this.imgModalCaption) this.imgModalCaption.textContent = title || '';
        this.imgModalPrevFocus = document.activeElement;
        this.imgModal.hidden = false;
        const dialog = this.imgModal.querySelector('.pa-img-modal__dialog');
        if (dialog) dialog.focus();
      }

      closeImgModal() {
        if (!this.imgModal || this.imgModal.hidden) return;
        this.imgModal.hidden = true;
        // Kein Body-Scroll-Lock aufheben: den haelt die Schnellansicht darunter weiter.
        if (this.imgModalImg) this.imgModalImg.removeAttribute('src');
        if (this.imgModalPrevFocus && this.imgModalPrevFocus.focus) this.imgModalPrevFocus.focus();
      }

      closeQuickView() {
        if (!this.modal || this.modal.hidden) return;
        this.closeImgModal();
        this.modal.hidden = true;
        document.body.classList.remove('overflow-hidden');
      }

      // Ausklappbares Detailpanel (techn. Daten + Downloads) je Zeile.
      initDetailsToggles() {
        this.items.forEach((item) => {
          const btn = item.querySelector('.pa-details-toggle');
          const panel = item.querySelector('.product-accessories__detail');
          if (!btn || !panel) return;
          btn.addEventListener('click', () => {
            const willOpen = panel.hidden;
            panel.hidden = !willOpen;
            btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
          });
        });
      }

      // Lager-Anzeige + techn.-Daten-Block auf die aktive Variante umstellen.
      updateVariantMeta(item) {
        const info = this.variantInfo(item);
        const stock = item.querySelector('.pa-stock');
        if (stock) {
          stock.textContent = info.available ? (stock.dataset.in || '') : (stock.dataset.out || '');
          stock.classList.toggle('pa-stock--out', !info.available);
        }
        const techs = item.querySelectorAll('.pa-tech[data-variant-id]');
        techs.forEach((t) => {
          t.hidden = String(t.dataset.variantId) !== String(info.id);
        });
      }

      // Eigenes Varianten-Dropdown (volle Farbkontrolle). Das native <select>.pa-variant bleibt
      // Datenquelle: Klick auf eine Option setzt dessen value + feuert 'change' → onVariantChange.
      initCustomSelects() {
        const closeAll = (except) => {
          this.querySelectorAll('.pa-cselect__list').forEach((l) => {
            if (l !== except) l.hidden = true;
          });
          this.querySelectorAll('.pa-cselect__btn').forEach((b) => b.setAttribute('aria-expanded', 'false'));
        };
        this.querySelectorAll('.pa-cselect').forEach((cs) => {
          const native = cs.querySelector('select.pa-cselect__native');
          const btn = cs.querySelector('.pa-cselect__btn');
          const valueEl = cs.querySelector('.pa-cselect__value');
          const list = cs.querySelector('.pa-cselect__list');
          if (!native || !btn || !valueEl || !list) return;
          const opts = Array.from(list.querySelectorAll('.pa-cselect__opt'));

          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const willOpen = list.hidden;
            closeAll(willOpen ? list : null);
            list.hidden = !willOpen;
            btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
          });

          opts.forEach((opt) => {
            opt.addEventListener('click', () => {
              if (opt.classList.contains('is-disabled')) return;
              native.value = opt.dataset.value;
              native.dispatchEvent(new Event('change', { bubbles: true }));
              valueEl.textContent = opt.textContent;
              opts.forEach((o) => {
                o.classList.remove('is-selected');
                o.removeAttribute('aria-selected');
              });
              opt.classList.add('is-selected');
              opt.setAttribute('aria-selected', 'true');
              list.hidden = true;
              btn.setAttribute('aria-expanded', 'false');
              btn.focus();
            });
          });
        });
        // Klick außerhalb / Escape schließt offene Dropdowns
        document.addEventListener('click', () => closeAll(null));
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') closeAll(null);
        });
      }

      // „Alle auswählen": schaltet alle verfügbaren Zubehör-Checkboxen; Pflicht-Zubehör bleibt an.
      initSelectAll() {
        this.selectAll = this.querySelector('.pa-check-all');
        if (!this.selectAll) return;
        this.selectAll.addEventListener('change', () => {
          const on = this.selectAll.checked;
          this.items.forEach((item) => {
            const cb = item.querySelector('.pa-check');
            if (!cb || cb.disabled) return;
            if (cb.dataset.required === 'true' && !on) return; // Pflicht nicht abwählen
            cb.checked = on;
          });
          this.onChange();
        });
      }

      // Zustand der „Alle auswählen"-Box an die Einzelauswahl angleichen (checked/indeterminate).
      syncSelectAll() {
        if (!this.selectAll) return;
        const boxes = this.items
          .map((i) => i.querySelector('.pa-check'))
          .filter((cb) => cb && !cb.disabled);
        const checked = boxes.filter((cb) => cb.checked).length;
        this.selectAll.checked = boxes.length > 0 && checked === boxes.length;
        this.selectAll.indeterminate = checked > 0 && checked < boxes.length;
      }

      // ---- Auswahl / Preise -------------------------------------------------

      variantInfo(item) {
        // Aktive Variante: <select> oder verstecktes Single-Feld.
        const sel = item.querySelector('select.pa-variant');
        let el, id, sku, available;
        if (sel) {
          el = sel.options[sel.selectedIndex];
          id = Number(sel.value);
          sku = (el && el.dataset.sku) || '';
          available = !(el && el.disabled);
        } else {
          el = item.querySelector('.pa-variant--single');
          id = el ? Number(el.value) : 0;
          sku = el ? el.dataset.sku || '' : '';
          available = true;
        }
        return { id: id, price: this.priceOf(el), sku: sku, available: available, el: el };
      }

      // Anzuzeigender/zu summierender Preis in Cents: B2B-Individualpreis (data-b2b-price),
      // falls für die SKU geladen, sonst Shopify-Listenpreis (data-price).
      priceOf(el) {
        if (!el) return 0;
        if (el.dataset.b2bPrice !== undefined && el.dataset.b2bPrice !== '') {
          return parseInt(el.dataset.b2bPrice, 10) || 0;
        }
        return parseInt(el.dataset.price, 10) || 0;
      }

      qtyFor(item) {
        const inp = item.querySelector('.pa-qty');
        const val = inp ? parseInt(inp.value, 10) : 1;
        return val > 0 ? val : 1;
      }

      isChecked(item) {
        const cb = item.querySelector('.pa-check');
        return !!(cb && cb.checked && !cb.disabled);
      }

      title(item) {
        const el = item.querySelector('.product-accessories__name');
        return el ? el.textContent.trim() : '';
      }

      onVariantChange(item) {
        // Preisanzeige, Lagerstatus und techn. Daten an die gewählte Variante angleichen.
        this.refreshPriceDisplay(item);
        this.updateVariantMeta(item);
        this.onChange();
      }

      // Setzt den Zeilenpreis (.pa-price) auf den aktiven Varianten-Preis (B2B oder Liste).
      refreshPriceDisplay(item) {
        const priceEl = item.querySelector('.pa-price');
        if (priceEl) priceEl.textContent = this.formatMoney(this.variantInfo(item).price);
      }

      onChange() {
        let grand = 0;
        let anyChecked = false;
        this.items.forEach((item) => {
          const checked = this.isChecked(item);
          item.classList.toggle('is-selected', checked);
          // Meterware: Gesamtlänge der Zeile („= 12 m") nachziehen. suq/base kommen von der
          // AKTIVEN Variante (Auswahl kann wechseln), Quelle sind deren data-su-*-Attribute.
          const ut = item.querySelector('[data-unit-total]');
          if (ut && window.SU) {
            const src = item.querySelector('.pa-variant, option:checked');
            if (src) {
              ut.setAttribute('data-suq', src.getAttribute('data-su-qty') || '0');
              ut.setAttribute('data-subase', src.getAttribute('data-su-base') || '');
            }
            window.SU.refreshUnitTotals(ut, checked ? this.qtyFor(item) : 0);
          }
          if (checked) {
            anyChecked = true;
            grand += this.qtyFor(item) * this.variantInfo(item).price;
          }
        });
        const formatted = this.formatMoney(grand);
        this.grandEls.forEach((el) => {
          el.textContent = formatted;
        });
        if (this.addAllBtn) this.addAllBtn.setAttribute('aria-disabled', anyChecked ? 'false' : 'true');
        this.syncSelectAll();
      }

      // ---- B2B-Individualpreise --------------------------------------------
      // Nur für eingeloggte B2B-Kunden (window.b2bConfig.isB2B). Nutzt die von
      // b2b-prices.js exportierten Helfer (window.B2BFetchPrices / B2BPriceCache).
      // Fehlen sie (Gast oder Skript noch nicht ausgeführt) → Listenpreis-Fallback,
      // keine Fehler. Reine Anzeige; verbindlich ist der Preis im Checkout-Gate.
      initB2BPrices() {
        const cfg = window.b2bConfig;
        if (!cfg || !cfg.isB2B) return;
        const run = (attempt) => {
          if (typeof window.B2BFetchPrices !== 'function') {
            if (attempt < 5) setTimeout(() => run(attempt + 1), 200); // Skript-Reihenfolge abwarten
            return;
          }
          this.loadB2BPrices();
        };
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => run(0));
        } else {
          run(0);
        }
      }

      // Alle preistragenden Elemente (Optionen + verstecktes Single-Feld) der Liste.
      priceEls() {
        return Array.from(this.querySelectorAll('.pa-variant--single[data-sku], option[data-sku]'));
      }

      loadB2BPrices() {
        const els = this.priceEls();
        const skus = [];
        els.forEach((el) => {
          const s = (el.dataset.sku || '').trim();
          if (s && skus.indexOf(s) === -1) skus.push(s);
        });
        if (!skus.length) return;

        // SWR Schritt 1: persistenten Cache SOFORT anwenden (kein Preis-Flash).
        if (window.B2BPriceCache) {
          const cached = {};
          skus.forEach((s) => {
            const hit = window.B2BPriceCache.get(s);
            if (hit) cached[s] = hit.price;
          });
          if (Object.keys(cached).length) this.applyB2BPrices(cached, els);
        }

        // SWR Schritt 2: live revalidieren und Cache aktualisieren.
        window.B2BFetchPrices(skus)
          .then((result) => {
            const prices = (result && result.prices) || {};
            this.applyB2BPrices(prices, els);
            if (window.B2BPriceCache) {
              const lvl = result && result.meta && result.meta.fallback_level;
              skus.forEach((s) => {
                if (prices[s] !== null && prices[s] !== undefined) {
                  window.B2BPriceCache.set(s, prices[s], lvl);
                }
              });
            }
          })
          .catch(function () {}); // Fehler → Listenpreis bleibt stehen
      }

      // Schreibt den B2B-Preis (Cents) je Element in data-b2b-price und aktualisiert
      // Zeilenpreise + Summe. Pack/Pro-Einheit-Heuristik NUR bei echter VE (su-qty > 1);
      // dann ist der Listenpreis (data-price) der VE-Packpreis als Referenz. Ohne VE ist
      // der B2B-Wert direkt der Stückpreis (typischer Zubehör-Fall).
      applyB2BPrices(prices, els) {
        (els || this.priceEls()).forEach((el) => {
          const sku = (el.dataset.sku || '').trim();
          const p = prices[sku];
          if (p === null || p === undefined) return; // kein B2B-Preis → Liste behalten
          const q = parseFloat(el.dataset.suQty || '0') || 0;
          const packChf = (parseInt(el.dataset.price, 10) || 0) / 100; // Gast-Packpreis (VE-Basis)
          // Pack- vs. Pro-Einheit-Entscheidung: EINE Wahrheit in assets/unit-price.js (window.SU).
          const chf = window.SU
            ? window.SU.normalize(p, packChf, q).packPrice
            : ((q > 1 && !(packChf > 0 && p > packChf / Math.sqrt(q))) ? p * q : p);
          el.dataset.b2bPrice = String(Math.round(Number((chf * 100).toPrecision(12))));
        });
        this.items.forEach((item) => this.refreshPriceDisplay(item));
        this.onChange();
      }

      // ---- Items für /cart/add ---------------------------------------------

      itemPayload(item) {
        const info = this.variantInfo(item);
        if (!info.id || !info.available) return null;
        return {
          id: info.id,
          quantity: this.qtyFor(item),
          properties: { Zubehör: 'zu ' + (this.title(item) || info.sku) },
        };
      }

      addSingle(item, btn) {
        const payload = this.itemPayload(item);
        if (!payload) {
          this.showError(window.customStrings.accUnavailable);
          return;
        }
        this.post([payload], btn);
      }

      addSelected() {
        if (this.addAllBtn && this.addAllBtn.getAttribute('aria-disabled') === 'true') return;
        const payloads = this.items
          .filter((item) => this.isChecked(item))
          .map((item) => this.itemPayload(item))
          .filter(Boolean);
        if (!payloads.length) {
          this.showError(window.customStrings.accSelect);
          return;
        }
        this.post(payloads, this.addAllBtn);
      }

      buildSections() {
        return this.cart && this.cart.getSectionsToRender
          ? this.cart.getSectionsToRender().map((s) => s.id)
          : [];
      }

      post(items, btn) {
        this.clearError();
        this.setLoading(btn, true);

        const body = {
          items: items,
          sections: this.buildSections(),
          sections_url: window.location.pathname,
        };

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);
        if (this.cart && this.cart.setActiveElement) this.cart.setActiveElement(document.activeElement);

        fetch(`${this.cartAddUrl}.js`, config)
          .then((response) => response.json().then((json) => ({ ok: response.ok, json: json })))
          .then(({ ok, json }) => {
            if (!ok || json.status) {
              this.showError(json.description || json.message || window.customStrings.addFailed);
              if (typeof publish === 'function') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-accessories',
                  errors: json.errors || json.description,
                  message: json.message,
                });
              }
              return;
            }
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-accessories', cartData: json });
            }
            if (this.cart && this.cart.renderContents) {
              if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              this.cart.renderContents(json);
            } else {
              window.location = (window.routes && window.routes.cart_url) || window.localizeUrl('/cart');
            }
          })
          .catch((e) => {
            console.error(e);
            this.showError('Netzwerkfehler. Bitte erneut versuchen.');
          })
          .finally(() => this.setLoading(btn, false));
      }

      // ---- UI-Helfer --------------------------------------------------------

      formatMoney(cents) {
        // Für B2B dasselbe Format wie b2b-prices.js (Tausender-Apostroph), sonst wie bisher.
        if (typeof window.B2BFormatPrice === 'function') {
          return window.B2BFormatPrice(cents / 100);
        }
        return 'CHF ' + (cents / 100).toFixed(2);
      }

      setLoading(btn, on) {
        if (!btn) return;
        btn.classList.toggle('loading', on);
        if (on) {
          btn.setAttribute('aria-disabled', 'true');
        } else if (btn === this.addAllBtn) {
          this.onChange();
        } else {
          btn.setAttribute('aria-disabled', 'false');
        }
      }

      showError(msg) {
        if (!this.errorEl) return;
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
    }
  );
}
